/**
 * adsConversion v0.4 — 2026-09-29 (cc_tasks row 218, T258): adsBeginCheckout(), the app's door to
 * the Google Ads "Begin checkout" conversion in public/google-tag.js (window.mfGoogleTag.beginCheckout).
 *
 * adsConversion v0.3 — 2026-09-28 (cc_tasks row 88, T129; v0.2 row 104, T145: adsPurchasePageview;
 * v0.3 row 158, T199: adsPurchasePageview for every paid order)
 *
 * The app's door to public/ads-conversions.js: Google Ads conversions for
 * signup_completed, checkout_started and paid. The script is loaded lazily, only
 * the first time one of these happens, and it does nothing unless the visitor
 * came from an ad, has not opted out (GPC / Do Not Track) and /ads-config.json
 * names a conversion id. Consent and "no PII" live in that one file.
 *
 * Every call resolves (never throws) within about a second, so it can be awaited
 * right before a redirect to checkout without holding the buyer up.
 */
export type AdsConversionKind = "signup_completed" | "checkout_started" | "paid";

interface MfAds {
  conversion: (kind: AdsConversionKind, opts?: { transaction_id?: string; value?: number; currency?: string }) => Promise<boolean>;
  purchasePageview?: (orderId: string, opts?: { value?: number; currency?: string }) => Promise<boolean>;
}

declare global {
  interface Window { mfAds?: MfAds }
}

let loading: Promise<MfAds | null> | null = null;

function loadScript(): Promise<MfAds | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.mfAds) return Promise.resolve(window.mfAds);
  if (!loading) {
    loading = new Promise((resolve) => {
      try {
        const s = document.createElement("script");
        s.async = true;
        s.src = "/ads-conversions.js";
        s.onload = () => resolve(window.mfAds ?? null);
        s.onerror = () => resolve(null);
        document.head.appendChild(s);
      } catch { resolve(null); }
    });
  }
  return loading;
}

/** Cheap pre-check so non-ad visitors never even fetch the script. */
function mayBeAdVisitor(): boolean {
  try {
    if (localStorage.getItem("mf_ads_click")) return true;
    const l = JSON.parse(localStorage.getItem("mf_last_touch") || "null");
    return !!(l && String(l.source).toLowerCase() === "google" && String(l.medium).toLowerCase() === "cpc");
  } catch { return false; }
}

export async function adsConversion(
  kind: AdsConversionKind,
  opts?: { transaction_id?: string; value?: number; currency?: string },
): Promise<boolean> {
  try {
    if (!mayBeAdVisitor()) return false;
    const ads = await Promise.race([
      loadScript(),
      new Promise<null>((r) => setTimeout(() => r(null), 1500)),
    ]);
    if (!ads) return false;
    return await Promise.race([
      ads.conversion(kind, opts),
      new Promise<boolean>((r) => setTimeout(() => r(false), 1500)),
    ]);
  } catch {
    return false;
  }
}

/**
 * The Purchase page load (T145; T199): Google Ads counts a Purchase as a page load of
 * /report-order/. Call ONLY for a paid order: the tag loads on that page (google-tag.js)
 * but sends no page view there on its own. transaction_id = the order id; value = the
 * amount Stripe charged, in dollars (not cents). Also sends the "paid" labelled
 * conversion when /ads-config.json has that label.
 */
export async function adsPurchasePageview(orderId: string, opts?: { value?: number; currency?: string }): Promise<boolean> {
  try {
    // T199: every paid order, not only ad visitors (Google decides attribution; the
    // script itself still sends nothing for GPC / Do Not Track).
    if (!orderId) return false;
    const ads = await Promise.race([
      loadScript(),
      new Promise<null>((r) => setTimeout(() => r(null), 3000)),
    ]);
    if (!ads?.purchasePageview) return false;
    return await ads.purchasePageview(orderId, opts);
  } catch {
    return false;
  }
}

/**
 * Google Ads "Begin checkout" (T258): await right before sending the visitor to Stripe.
 * The one sender is window.mfGoogleTag.beginCheckout in public/google-tag.js (every shipped
 * page loads it); it sends the conversion unless the visitor opted out (GPC / Do Not Track).
 * Resolves on Google's callback or within about 1.2 s, and at once when the tag is absent
 * (dev server, blocked). Never throws, so the checkout always opens.
 */
export function adsBeginCheckout(): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };
    setTimeout(finish, 1500);
    try {
      const g = (window as unknown as { mfGoogleTag?: { beginCheckout?: (go: () => void) => void } }).mfGoogleTag;
      if (g && typeof g.beginCheckout === "function") g.beginCheckout(finish); else finish();
    } catch { finish(); }
  });
}

/**
 * signup_completed, once per browser, for an account created in the last 24 hours
 * (email signups confirm later, Google signups land straight in). Called from
 * useAuth on every session restore; the script de-duplicates.
 */
export function adsSignupIfNew(createdAt: string | undefined | null): void {
  if (!createdAt) return;
  const t = Date.parse(createdAt);
  if (!(t > 0) || Date.now() - t > 24 * 60 * 60 * 1000) return;
  void adsConversion("signup_completed");
}
